import _ from 'lodash';
import {
  confirmLexicalStateInCTAComponent,
  confirmLexicalStateInTextComponent,
  findLayeredTextRelationByDocumentId,
} from 'modules/draftjs2lexical';
import {
  convertLexicalCTAEditorStateToHtml,
  convertLexicalTextEditorStateToHtml,
} from 'modules/Lexical/Translation';
import { batchActions } from 'redux-batched-actions';
import { Task } from 'redux-saga';
import { call, cancel, delay, fork, put, select, spawn } from 'redux-saga/effects';
import { Layer, MARCAPS_PORTALS, MethodName, ProcessType } from 'const';
import { TRANSLATION_SUB_STEPS } from 'const/translationSubStatus';
import * as AppActions from 'containers/App/actions';
import { translateGroupReusableLayouts } from 'containers/App/services/translateGroupReusableLayouts';
import { translateReusableLayouts } from 'containers/App/services/translateReusableLayouts';
import { setArtboards } from 'containers/Artboards/actions';
import { artboards as artboardsSelector } from 'containers/Artboards/selectors';
import { projectFlatFonts as projectFlatFontsSelector } from 'containers/BrandDefinition/selectors';
import * as commonSelectors from 'containers/Common/selectors';
import { downloadImages, setDocuments } from 'containers/Documents/actions';
import * as documentsSelectors from 'containers/Documents/selectors';
import { setLayouts } from 'containers/Layouts/actions';
import { layeredLayouts as layeredLayoutsSelector } from 'containers/Layouts/selectors';
import { setActiveLayer as createSetActiveLayerAction } from 'containers/Project/actions';
import { setActiveLayer } from 'containers/Project/sagas/setActiveLayer';
import * as ProjectSelectors from 'containers/Project/selectors';
import { getDocumentsOnArtboards } from 'containers/Project/services/getDocumentsOnArtboards';
import { setRelations } from 'containers/Relations/actions';
import * as relationsSelectors from 'containers/Relations/selectors';
import {
  rootDocument as rootDocumentSelector,
  targetLanguage as targetLanguagesSelector,
} from 'containers/RootDocument/selectors';
import * as Models from 'models';
import { fetchTranslationStatus, oneStepTranslationPost, pollResponseUri } from 'services/api';
import logger from 'services/logger';
import { Notifications } from 'services/Notifications';
import { TranslationService } from 'services/Translation';
import { hasAITranslationDontAskAgain, setAITranslationDontAskAgain } from 'services/userPreferences';
import { isMockDocument } from 'utils/documents';
import { isCallToAction, isLayoutRendition, isTextComponent, isImage } from 'utils/entityType';
import { intlGet } from 'utils/intlGet';
import { isGroupLayout } from 'utils/layouts/isGroupLayout';
import { isReusableLayout } from 'utils/reusableLayouts/isReusableLayout';

function hasLayoutBeenTranslated(
  layout: Models.LayeredLayoutMap,
  documents: Models.CombinedDocumentsMap,
  translatedDocumentIdsByOriginal: Record<string, string>,
): boolean {
  const layoutDocument = documents.get(layout.getIn(['documentId', Layer.ORIGINAL])) as Models.ReusableLayoutMap;
  const layoutChildDocuments = layoutDocument.get('documents');

  // empty RLs are considered translated (https://issues.merck.com/browse/DCC-5141)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return layoutChildDocuments.size === 0 || layoutChildDocuments.some((document: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const docId = document.get('id');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return !!translatedDocumentIdsByOriginal[docId];
  });
}

function hasGroupLayoutBeenTranslated(
  groupLayout: Models.LayeredGroupLayoutMap,
  layouts: Models.LayeredCombinedLayoutsMap,
  documents: Models.CombinedDocumentsMap,
  translatedDocumentIdsByOriginal: Record<string, string>,
): boolean {
  const layoutDocument = documents.get(groupLayout.getIn(['documentId', Layer.TRANSLATED]));

  if (layoutDocument && !isMockDocument(layoutDocument)) {
    return true;
  }

  const layoutIds = groupLayout.get('layoutIds');

  return layoutIds.some(
    (layoutId) => {
      const layout = layouts.get(layoutId) as Models.LayeredLayoutMap;

      return hasLayoutBeenTranslated(layout, documents, translatedDocumentIdsByOriginal);
    },
  );
}

function canGroupLayoutBeCreated(
  groupLayout: Models.LayeredGroupLayoutMap,
  reusableLayouts: Models.LayeredCombinedLayoutsMap,
  documents: Models.CombinedDocumentsMap,
): boolean {
  const layoutIds = groupLayout.get('layoutIds');

  return layoutIds.every((layoutId) => {
    const layoutDocumentId = reusableLayouts.getIn([layoutId, 'documentId', Layer.TRANSLATED]);
    const layoutDocument = documents.get(layoutDocumentId);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    return layoutDocument && !(layoutDocument as any).get('isMockDocument') && !!(layoutDocument as any).get('documentId');
  });
}

const STATUS_POLL_INTERVAL_MS = 1500;

interface TranslationFailureResult {
  status: 'failed';
  error?: string;
  errorCode?: string;
  processedAt?: string;
}

function isTranslationFailureResult(value: unknown): value is TranslationFailureResult {
  return _.isPlainObject(value) && _.get(value, 'status') === 'failed';
}

const marcapsLink = `<a href="${MARCAPS_PORTALS}" target="_blank" rel="noopener noreferrer">MARCAPS</a>`;

const TRANSLATION_ERROR_CODE_MESSAGES: Record<string, { title: string; message: string }> = {
  GENERIC_WORKFLOW_FAILURE: {
    title: intlGet('AITranslation', 'ErrorTitle'),
    // eslint-disable-next-line max-len
    message: `Something went wrong with the translation. Please try again later. If the problem or any bias persists, please submit a ticket in ${marcapsLink}.`,
  },
  UNSUPPORTED_LANGUAGE: {
    title: intlGet('AITranslation', 'ErrorTitle'),
    // eslint-disable-next-line max-len
    message: `One or both of the selected languages are not supported by translateGPTeal. Alternatively, you can translate directly within the document or utilize another translation service by exporting the translation package. If you would like to request support for a language that is not currently available, please raise a ticket in ${marcapsLink}.`,
  },
};

function parseTranslationErrorMessage(error: unknown, errorCode?: string): { title: string; message: string } {
  if (errorCode && TRANSLATION_ERROR_CODE_MESSAGES[errorCode]) {
    return TRANSLATION_ERROR_CODE_MESSAGES[errorCode];
  }

  const fallbackTitle = intlGet('AITranslation', 'ErrorTitle');
  const fallbackMessage = intlGet('AITranslation', 'ErrorMessage');
  const rawMessage = _.trim((error as Error | undefined)?.message ?? '');

  if (!rawMessage) {
    return {
      title: fallbackTitle,
      message: fallbackMessage,
    };
  }

  const normalizedLines = rawMessage
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  // If backend sends a title in the first line, display it as modal title.
  if (normalizedLines.length > 1) {
    return {
      title: normalizedLines[0],
      message: normalizedLines.slice(1).join('\n'),
    };
  }

  return {
    title: fallbackTitle,
    message: rawMessage,
  };
}

function* pollTranslationStatus(statusUri: string, currentStep: { value: number }): Generator {
  while (currentStep.value < 4) {
    yield delay(STATUS_POLL_INTERVAL_MS);

    try {
      const status = (yield call(fetchTranslationStatus, statusUri)) as { step: number; message: string } | null;

      if (status && status.step > currentStep.value) {
        currentStep.value = status.step;
        yield put(AppActions.updateAppState({ loadingOverlayOptions: { info: status.message } }));
      }
    } catch {
      // Network/timeout errors — silently ignore, don't affect translation
    }
  }
}

export function* aiTranslateSaga(): Generator {
  const logId = logger.performanceStart();
  const methodName = MethodName.AI_TRANSLATION;
  let statusTask: Task | null = null;

  try {
    // Check if user has previously selected "don't ask again"
    const hasUserDisabledConfirmation: boolean = yield call(
      hasAITranslationDontAskAgain,
    );

    if (!hasUserDisabledConfirmation) {
      // Show confirmation dialog with "don't ask again" option
      const { accepted, doNotAskAgain }: { accepted: boolean; doNotAskAgain: boolean } = yield call(
        [Notifications, Notifications.showConfirmation],
        {
          title: intlGet('AITranslation', 'ConfirmationTitle'),
          message: intlGet('AITranslation', 'ConfirmationMessage'),
          messageLinks: { MARCAPS: MARCAPS_PORTALS },
          acceptLabel: intlGet('AITranslation', 'Confirm'),
          declineLabel: intlGet('Common', 'Cancel'),
          disableClickOutside: true,
          showDoNotAskAgain: true,
          className: 'aiTranslationConfirmation',
        },
        'aiTranslationConfirmation',
      );

      if (!accepted) {
        return;
      }

      // Save "don't ask again" preference if user checked it and accepted
      if (doNotAskAgain) {
        yield call(setAITranslationDontAskAgain, true);
      }
    }

    // STEP 1: Prepare translation data (from generateTranslationPackage)
    const usedRelations = (yield select(documentsSelectors.relationsOnScreens)) as ReturnTypeSaga<typeof documentsSelectors.relationsOnScreens>;
    const isLayoutRenditionOnArtboards = usedRelations.some(relation => isLayoutRendition(relation) && relation.get('documentId').some(Boolean));

    if (isLayoutRenditionOnArtboards) {
      yield spawn([logger, logger.performanceCancel], logId);

      yield call(
        [Notifications, Notifications.showNotification],
        {
          title: intlGet('Notification.Translation.ReusableLayoutPreview', 'Title'),
          message: intlGet('Notification.Translation.ReusableLayoutPreview', 'Message'),
        },
      );

      return;
    }

    yield put(AppActions.lockProjectContent(ProcessType.AI_TRANSLATION));

    // Sub-status step 1: Preparing data
    const subStep = { value: 1 };
    yield put(AppActions.updateAppState({ loadingOverlayOptions: { info: TRANSLATION_SUB_STEPS[1].message } }));

    const currentActiveLayer = Layer.ORIGINAL;
    // to fetch correct documents from RLs
    yield call(setActiveLayer, createSetActiveLayerAction(currentActiveLayer));

    const rootDocument = (yield select(rootDocumentSelector)) as ReturnTypeSaga<typeof rootDocumentSelector>;
    const targetLanguages = (yield select(targetLanguagesSelector)) as ReturnTypeSaga<typeof targetLanguagesSelector>;
    const targetLanguageForTranslation = targetLanguages?.[0] || 'English';

    const documentsList = (yield call(
      getDocumentsOnArtboards,
      ProcessType.AI_TRANSLATION,
    )) as ReturnTypeSaga<typeof getDocumentsOnArtboards>;
    const documents = documentsList.toJS() as Models.CombinedDocuments;
    const relations = (yield select(relationsSelectors.layeredRelations)) as ReturnTypeSaga<typeof relationsSelectors.layeredRelations>;
    const projectFlatFonts = (yield select(projectFlatFontsSelector)) as ReturnTypeSaga<typeof projectFlatFontsSelector>;
    const projectType = (yield select(ProjectSelectors.projectType)) as ReturnTypeSaga<typeof ProjectSelectors.projectType>;

    const textComponentHtmlById = _(documents)
      .pickBy<Models.TextComponent>(isTextComponent)
      .mapValues((textComponent) => {
        const relation = findLayeredTextRelationByDocumentId(relations, textComponent.id, currentActiveLayer);
        const styles = relation?.getIn(['styles', currentActiveLayer])?.toJS() as Models.TextRelationStyles | undefined;
        const { lexicalState } = confirmLexicalStateInTextComponent(textComponent, styles ?? null);

        return convertLexicalTextEditorStateToHtml(lexicalState ?? undefined);
      })
      .value();

    const callToActionsInnerTextById = _(documents)
      .pickBy<Models.CallToAction>(isCallToAction)
      .mapValues((ctaComponent) => {
        const relation = findLayeredTextRelationByDocumentId(relations, ctaComponent.id, currentActiveLayer);
        const { lexicalState } = confirmLexicalStateInCTAComponent(
          ctaComponent,
          relation?.getIn(['styles', currentActiveLayer])?.toJS() as Models.CallToActionStyles | undefined,
          projectType as string,
          projectFlatFonts,
        );
        const html = convertLexicalCTAEditorStateToHtml(lexicalState);

        return html ?? ctaComponent.name;
      })
      .value();

    // STEP 2: Call AI translation API (POST only, no auto-polling)
    const postResponse = (yield call(
      oneStepTranslationPost,
      rootDocument.toJS() as Models.RootDocument,
      documents,
      textComponentHtmlById,
      callToActionsInnerTextById,
      targetLanguageForTranslation,
    )) as Awaited<ReturnType<typeof oneStepTranslationPost>>;

    const { responseUri, statusUri, retryAttemtps, retryTimeoutSeconds } = postResponse;

    // Start polling statusUri in parallel
    if (statusUri) {
      statusTask = (yield fork(pollTranslationStatus, statusUri, subStep)) as Task;
    }

    // Poll responseUri for the final translation result
    const translationResult = (yield call(
      pollResponseUri,
      responseUri,
      retryAttemtps,
      retryTimeoutSeconds,
    )) as Awaited<ReturnType<typeof pollResponseUri>> | TranslationFailureResult;

    // Stop status polling once responseUri returns
    if (statusTask) {
      yield cancel(statusTask);
      statusTask = null;
    }

    if (isTranslationFailureResult(translationResult)) {
      const failureError = new Error(_.trim(translationResult.error ?? '') || intlGet('AITranslation', 'ErrorMessage'));
      (failureError as Error & { translationErrorCode?: string }).translationErrorCode = translationResult.errorCode;
      throw failureError;
    }

    if (!('data' in translationResult) || !translationResult.data) {
      throw new Error('AI translation failed: No data received');
    }

    // Sub-status step 5: Applying translations
    subStep.value = 5;
    yield put(AppActions.updateAppState({ loadingOverlayOptions: { info: TRANSLATION_SUB_STEPS[5].message } }));

    const translatedDocumentsMap = translationResult.data.documentsMap;

    // STEP 3: Import translation result (from importTranslationPackage)
    yield put(AppActions.stopHandlingReusableLayoutsEditing());

    // to prevent different import errors
    yield call(setActiveLayer, createSetActiveLayerAction(Layer.ORIGINAL));

    const rootDocumentId = rootDocument.get('documentId');

    // Note: Images and references handling from importTranslationPackage not needed for AI translation
    // as we directly get translated documents from the API

    const originalDocuments = (
      (yield select(documentsSelectors.documents)) as ReturnTypeSaga<typeof documentsSelectors.documents>
    ).toJS() as Models.CombinedDocuments;
    const layeredRelationsData = (
      (yield select(relationsSelectors.layeredRelations)) as ReturnTypeSaga<typeof relationsSelectors.layeredRelations>
    ).toJS() as Models.LayeredRelations;
    const layeredLayoutsData = (
      (yield select(layeredLayoutsSelector)) as ReturnTypeSaga<typeof layeredLayoutsSelector>
    ).toJS() as Models.LayeredLayouts;
    const artboards = ((yield select(artboardsSelector)) as ReturnTypeSaga<typeof artboardsSelector>).toJS() as Models.Artboards;

    const sectionStyles: ReturnTypeSaga<typeof commonSelectors.sectionStylesByRelationId> = yield select(commonSelectors.sectionStylesByRelationId);
    const colorsByRelationId: ReturnTypeSaga<typeof commonSelectors.flatColorsByRelationId> = yield select(commonSelectors.flatColorsByRelationId);
    const fontsByRelationId: ReturnTypeSaga<typeof commonSelectors.flatFontsByRelationId> = yield select(commonSelectors.flatFontsByRelationId);
    const brandStylesByRelationId: ReturnTypeSaga<typeof commonSelectors.brandStylesByRelationId> =
      yield select(commonSelectors.brandStylesByRelationId);

    const translationService = new TranslationService(
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        brandStylesByRelationId: brandStylesByRelationId as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colorsByRelationId: colorsByRelationId as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fontsByRelationId: fontsByRelationId as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sectionStylesByRelationId: sectionStyles as any,
      },
    );

    const {
      relations: relationToSet,
      documents: documentsToSet,
      layouts: layoutsToSet,
      artboards: artboardsToSet,
      translatedDocumentIdsByOriginal,
    }: ReturnTypeSaga<typeof translationService.applyTranslations> = yield call(
      [translationService, translationService.applyTranslations],
      translatedDocumentsMap,
      originalDocuments,
      layeredRelationsData,
      layeredLayoutsData,
      artboards,
    );

    const newTranslatedImages = _.pickBy<Models.CombinedDocument, Models.Image>(documentsToSet, isImage);

    yield put(downloadImages(newTranslatedImages));
    yield put(batchActions([
      setDocuments(documentsToSet),
      setRelations(relationToSet),
      setLayouts(layoutsToSet),
      setArtboards(artboardsToSet),
    ]));

    yield call(setActiveLayer, createSetActiveLayerAction(Layer.TRANSLATED), true);

    const updatedDocuments: ReturnTypeSaga<typeof documentsSelectors.documents> = yield select(documentsSelectors.documents);
    const updatedLayeredLayouts: ReturnTypeSaga<typeof layeredLayoutsSelector> = yield select(layeredLayoutsSelector);

    // all RLs from original layer that could be translated
    const reusableLayouts = updatedLayeredLayouts
      .filter(layout => isReusableLayout(layout, updatedDocuments, Layer.ORIGINAL)) as Models.LayeredLayoutsMap;
    // RLs that could be created as translated RLs
    const translatedReusableLayouts = reusableLayouts
      .filter(layout => hasLayoutBeenTranslated(layout, updatedDocuments, translatedDocumentIdsByOriginal));

    const translatedReusableLayoutDocuments: ReturnTypeSaga<typeof translateReusableLayouts> = yield call(
      translateReusableLayouts,
      rootDocumentId,
      reusableLayouts,
      translatedReusableLayouts,
      updatedDocuments.toJS() as Models.CombinedDocuments,
    );

    const updatedDocumentsWithRLs: ReturnTypeSaga<typeof documentsSelectors.documents> = yield select(documentsSelectors.documents);
    const updatedLayouts: ReturnTypeSaga<typeof layeredLayoutsSelector> = yield select(layeredLayoutsSelector);

    // Handle Group Reusable Layouts
    try {
      // all GRLs from original layer that could be translated
      const groupReusableLayouts = updatedLayouts.filter((layout) => {
        const document = updatedDocumentsWithRLs.get(layout.getIn(['documentId', Layer.ORIGINAL])) as Models.ReusableLayoutMap;

        return isGroupLayout(layout) && document && !document.get('isMockDocument');
      });

      // GRLS from target layer that could be created as translated GRLs
      const translatedGroupReusableLayouts = updatedLayouts
        .filter(layout => isGroupLayout(layout))
        .filter(groupLayout =>
          hasGroupLayoutBeenTranslated(
            groupLayout as Models.LayeredGroupLayoutMap,
            updatedLayouts,
            updatedDocumentsWithRLs,
            translatedDocumentIdsByOriginal,
          ),
        )
        .filter(groupLayout =>
          canGroupLayoutBeCreated(
            groupLayout as Models.LayeredGroupLayoutMap,
            updatedLayouts,
            updatedDocumentsWithRLs,
          ),
        );

      if (groupReusableLayouts.size > 0 && translatedGroupReusableLayouts.size > 0) {
        yield call(
          translateGroupReusableLayouts,
          rootDocumentId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          groupReusableLayouts as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          translatedGroupReusableLayouts as any,
          updatedDocumentsWithRLs.toJS() as Models.CombinedDocuments,
          translatedReusableLayoutDocuments,
        );
      }
    } catch (error) {
      // Log the error but don't fail the entire translation process
      yield spawn([logger, logger.error], error, logId, { methodName: 'aiTranslateSaga.groupLayoutTranslation' });
    }

    yield spawn([logger, logger.performanceEnd], logId, { methodName });

    yield call(
      [Notifications, Notifications.success],
      intlGet('Notification.Success', 'TranslationPackageHasBeenImported'),
      intlGet('Notification', 'Success'),
    );
  } catch (error) {
    const errorCode = (error as Error & { translationErrorCode?: string })?.translationErrorCode;
    const parsedMessage = parseTranslationErrorMessage(error, errorCode);

    yield spawn([logger, logger.error], error, logId, { methodName });
    yield spawn(
      [Notifications, Notifications.showNotification],
      {
        title: parsedMessage.title,
        message: parsedMessage.message,
      },
    );
  } finally {
    if (statusTask) {
      yield cancel(statusTask);
    }
    yield put(AppActions.updateAppState({ loadingOverlayOptions: { info: undefined } }));
    yield put(AppActions.unlockProjectContent());
    yield put(AppActions.startHandlingReusableLayoutsEditing());
  }
}
